import React, { useCallback, useEffect, useRef } from 'react';

import { ExtendedMarkerData, TriggerType } from '../../types';

export const useMapTriggers = ({
	map,
	markers,
	infoWindow,
	unsubscribeFromMapEvents
}: {
	map: google.maps.Map | null;
	markers: ExtendedMarkerData[];
	infoWindow: google.maps.InfoWindow;
	unsubscribeFromMapEvents: React.MutableRefObject<google.maps.MapsEventListener[]>;
}) => {
	const activeTriggerTypes = useRef<Set<TriggerType>>(new Set());

	// Function to get unique trigger types from markers
	const getUniqueTriggerTypes = useCallback((markersList: ExtendedMarkerData[]): TriggerType[] => {
		const triggerTypes = new Set<TriggerType>();
		markersList.forEach(marker => {
			if (marker.tooltipOptions?.trigger) {
				triggerTypes.add(marker.tooltipOptions.trigger);
			}
		});
		return Array.from(triggerTypes);
	}, []);

	// Function to check if we already have a listener for a specific trigger type
	const hasListenerForTriggerType = (triggerType: TriggerType): boolean => {
		return activeTriggerTypes.current.has(triggerType);
	};

	// Function to add map-level listener for a trigger type
	const addMapListenerForTriggerType = useCallback(
		(triggerType: TriggerType) => {
			if (!map) return;

			unsubscribeFromMapEvents.current.push(
				google.maps.event.addListener(map, triggerType, () => {
					infoWindow.close();
				})
			);
			activeTriggerTypes.current.add(triggerType);
		},
		[map, infoWindow, unsubscribeFromMapEvents]
	);

	// Effect to manage trigger listeners based on markers
	useEffect(() => {
		if (!map || !markers?.length) {
			return;
		}

		const uniqueTriggerTypes = getUniqueTriggerTypes(markers);

		uniqueTriggerTypes.forEach(triggerType => {
			if (!hasListenerForTriggerType(triggerType)) {
				addMapListenerForTriggerType(triggerType);
			}
		});
	}, [map, markers, addMapListenerForTriggerType, getUniqueTriggerTypes]);
};
